import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { X } from 'lucide-react';

const DecisionModal = ({ isOpen, onDecision, waiting = false, onWaitingTimeout, syncStartTime }) => {
  const [displayTime, setDisplayTime] = useState(60);
  const [waitingTime, setWaitingTime] = useState(60);
  const timerRef = useRef(null);
  const waitingTimerRef = useRef(null);
  const waitingStartTimeRef = useRef(null); // Время начала ожидания (отдельно от syncStartTime)

  // Обработка закрытия через крестик = "Нет"
  const handleCloseClick = useCallback(() => {
    console.log('Close button clicked - treating as NO');
    onDecision(false);
  }, [onDecision]);

  // Стабильная функция для обработки timeout
  const handleTimeout = useCallback(() => {
    onDecision(false);
  }, [onDecision]);

  // Стабильная функция для обработки waiting timeout
  const handleWaitingTimeout = useCallback(() => {
    if (onWaitingTimeout) {
      onWaitingTimeout();
    }
  }, [onWaitingTimeout]);

  // Таймер обратного отсчёта для принятия решения (синхронизированный)
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!isOpen || waiting) {
      setDisplayTime(60);
      return;
    }

    // Вычисляем оставшееся время на основе синхронизированного времени
    const calculateRemainingTime = () => {
      if (syncStartTime) {
        const startTime = new Date(syncStartTime).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        return Math.max(0, 60 - elapsed);
      }
      return 60; // Если нет syncStartTime, начинаем с 60
    };

    const initialTime = calculateRemainingTime();
    setDisplayTime(initialTime);

    if (initialTime <= 0) {
      handleTimeout();
      return;
    }

    // Запускаем интервал - обновляем каждую секунду
    timerRef.current = setInterval(() => {
      setDisplayTime(prev => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          // Используем setTimeout чтобы не вызывать setState во время рендера
          setTimeout(() => handleTimeout(), 0);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, waiting, handleTimeout, syncStartTime]);

  // Таймер для режима ожидания решения партнёра
  useEffect(() => {
    if (waitingTimerRef.current) {
      clearInterval(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }

    if (!isOpen || !waiting) {
      setWaitingTime(60);
      waitingStartTimeRef.current = null;
      return;
    }

    // При переходе в режим ожидания - начинаем новый отсчёт с 60 секунд
    // НЕ используем syncStartTime, потому что это время показа модалки, а не время начала ожидания
    if (!waitingStartTimeRef.current) {
      waitingStartTimeRef.current = Date.now();
    }

    setWaitingTime(60);

    // Запускаем интервал - обновляем каждую секунду
    waitingTimerRef.current = setInterval(() => {
      setWaitingTime(prev => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          if (waitingTimerRef.current) {
            clearInterval(waitingTimerRef.current);
            waitingTimerRef.current = null;
          }
          setTimeout(() => handleWaitingTimeout(), 0);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (waitingTimerRef.current) {
        clearInterval(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
    };
  }, [isOpen, waiting, handleWaitingTimeout, syncStartTime]);

  return (
    <Dialog open={isOpen} modal={true}>
      <DialogContent 
        className="sm:max-w-md" 
        style={{ zIndex: 10000 }}
        data-testid="decision-modal"
        hideCloseButton={true}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Кастомная кнопка закрытия = НЕТ (только если не в режиме ожидания) */}
        {!waiting && (
          <button
            type="button"
            onClick={handleCloseClick}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-50"
            data-testid="decision-close-button"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Закрыть</span>
          </button>
        )}
        
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center" style={{ color: '#1F1F1F' }}>
            {waiting ? 'Ожидаем решения...' : 'Хотите продолжить общаться в чате?'}
          </DialogTitle>
        </DialogHeader>

        {waiting ? (
          <div className="flex flex-col items-center py-6">
            <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-[#7A7A7A]">Ожидаем решения собеседника...</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
              <span className="text-xl font-mono font-bold" style={{ color: waitingTime <= 10 ? '#FF5757' : '#1F1F1F' }}>
                {waitingTime}
              </span>
              <span className="text-[#7A7A7A] text-sm">сек</span>
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => onDecision(true)}
                className="flex-1 py-6 rounded-full text-white font-semibold text-lg"
                style={{ background: 'linear-gradient(135deg, #34C759 0%, #5DD97C 100%)' }}
                data-testid="decision-yes-button"
              >
                ДА
              </Button>
              
              <Button
                onClick={() => onDecision(false)}
                className="flex-1 py-6 rounded-full text-white font-semibold text-lg"
                style={{ background: 'linear-gradient(135deg, #FF5757 0%, #FF7B7B 100%)' }}
                data-testid="decision-no-button"
              >
                НЕТ
              </Button>
            </div>

            <div className="text-center mt-4">
              <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                <span className="text-2xl font-mono font-bold" style={{ color: displayTime <= 10 ? '#FF5757' : '#1F1F1F' }}>
                  {displayTime}
                </span>
                <span className="text-[#7A7A7A] text-sm">сек</span>
              </div>
              <p className="text-[#7A7A7A] text-sm mt-2">
                У вас есть 1 минута для принятия решения
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DecisionModal;
